import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { TextField, useForm, Chip } from '@frontend/design-system';

const MAX_INSURANCE_NAME_LENGTH = 100;

type Props = {
  value?: string[];
  onChange: (value: string[]) => void;
  onErrorChange?: (hasError: boolean) => void;
  isRequired?: boolean;
};

export const InsuranceInput = ({ value = [], onChange, onErrorChange, isRequired = true }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const { getFieldProps, values, seedValues } = useForm({
    fields: {
      insuranceName: {
        type: 'text',
        value: '',
        required: false,
        validateOnChange: true,
        validator: (field) => {
          if (field.value && field.value.trim().length > MAX_INSURANCE_NAME_LENGTH) {
            return t('Insurance name must be less than {{maxLength}} characters', {
              maxLength: MAX_INSURANCE_NAME_LENGTH,
            });
          }
          return '';
        },
      },
    },
  });

  const fieldProps = getFieldProps('insuranceName');
  const isInternalUpdate = useRef(false);

  const handleAdd = () => {
    const trimmed = ((values.insuranceName as string) || '').trim();
    if (trimmed && !value.includes(trimmed)) {
      isInternalUpdate.current = true;
      onChange([...value, trimmed]);
      seedValues({ insuranceName: '' });
    }
  };

  const handleRemove = (itemToRemove: string) => {
    isInternalUpdate.current = true;
    onChange(value.filter((item) => item !== itemToRemove));
  };

  useEffect(() => {
    const listHasError = isRequired && value.length === 0;
    const inputHasError = !!fieldProps.error;
    onErrorChange?.(listHasError || inputHasError);
  }, [value, fieldProps.error, isRequired, onErrorChange]);

  return (
    <div css={styles.container}>
      <TextField
        {...fieldProps}
        name='insuranceName'
        label={t('Insurance Name')}
        placeholder={t('Type insurance name and click Add...')}
        actionText={t('Add')}
        onActionClick={handleAdd}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
          }
        }}
      />
      {value.length > 0 && (
        <div css={styles.chipsContainer}>
          {value.map((item) => (
            <Chip.Removable key={item} onClick={() => handleRemove(item)}>
              {item}
            </Chip.Removable>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
  `,
  chipsContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--fabric-space-gap-xs);
    margin-top: var(--fabric-space-gap-xs);
  `,
};
